import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { api, AuthError } from '@/lib/api';

interface Subcategory {
  key: string;
  label: string;
}

interface Category {
  key: string;
  label: string;
  subcategories?: Subcategory[];
}

const CATEGORIES: Category[] = [
  {
    key: 'getting-started',
    label: 'Getting Started',
    subcategories: [
      { key: 'farmer-account-setup', label: 'Account & farm setup' },
      { key: 'plans', label: 'Plans' },
      { key: 'troubleshooting', label: 'Troubleshooting' },
    ],
  },
  {
    key: 'marketplace',
    label: 'Marketplace',
    subcategories: [
      { key: 'shopping', label: 'Shopping' },
      { key: 'products', label: 'Products' },
      { key: 'locations-and-schedules', label: 'Locations & schedules' },
      { key: 'shop-settings', label: 'Shop settings' },
      { key: 'marketing-and-growth', label: 'Marketing & growth' },
    ],
  },
  {
    key: 'csa',
    label: 'CSA Shares',
    subcategories: [
      { key: 'csa-setup', label: 'Setting up shares' },
      { key: 'custom-shares', label: 'Custom (box-choice) shares' },
      { key: 'csa-management', label: 'Managing your CSA' },
    ],
  },
  {
    key: 'register',
    label: 'Register (POS)',
    subcategories: [
      { key: 'register-setup', label: 'Setup & hardware' },
      { key: 'register-products', label: 'Products on Register' },
      { key: 'register-transactions', label: 'Running transactions' },
    ],
  },
  {
    key: 'wholesale',
    label: 'Wholesale',
    subcategories: [
      { key: 'wholesale-setup', label: 'Getting started with Wholesale' },
      { key: 'wholesale-products', label: 'Wholesale products' },
      { key: 'wholesale-customers', label: 'Managing wholesale customers' },
      { key: 'wholesale-orders', label: 'Wholesale orders & fulfillment' },
    ],
  },
  {
    key: 'orders-and-fulfillment',
    label: 'Orders & Fulfillment',
    subcategories: [
      { key: 'managing-orders', label: 'Managing orders' },
      { key: 'fulfillment', label: 'Packing & distribution' },
      { key: 'refunds-and-cancellations', label: 'Refunds & cancellations' },
    ],
  },
  {
    key: 'payments-and-billing',
    label: 'Payments & Billing',
    subcategories: [
      { key: 'financial-reports', label: 'Financial reports & exports' },
      { key: 'payment-methods', label: 'Payment methods' },
      { key: 'invoices', label: 'Invoices & collections' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    subcategories: [
      { key: 'customer-accounts', label: 'Customer accounts' },
      { key: 'farm-credit', label: 'Farm credit' },
      { key: 'notifications', label: 'Notifications' },
    ],
  },
  { key: 'account-and-settings', label: 'Account & Settings' },
  { key: 'whats-new', label: "What's New" },
];

type ContentSource = 'scratch' | 'paste' | 'claude';

interface HtmlCtx { inHeading: boolean; listDepth: number; }

function parseFontSizePx(style: string): number {
  const m = style.match(/font-size\s*:\s*([\d.]+)(px|pt|em|rem)?/i);
  if (!m) return 0;
  const v = parseFloat(m[1]);
  switch ((m[2] ?? 'px').toLowerCase()) {
    case 'pt':  return v * 1.333;
    case 'em':
    case 'rem': return v * 16;
    default:    return v;
  }
}

function headingPrefixFromPx(px: number): string {
  if (px >= 24) return '# ';
  if (px >= 18) return '## ';
  if (px >= 15) return '### ';
  return '';
}

function convertNode(node: Node, ctx: HtmlCtx = { inHeading: false, listDepth: 0 }): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/\s+/g, ' ');
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  const style = el.getAttribute('style') ?? '';
  const inner = (c?: Partial<HtmlCtx>) =>
    Array.from(el.childNodes).map(n => convertNode(n, { ...ctx, ...c })).join('');

  switch (tag) {
    case 'strong':
    case 'b': {
      // Google Docs wraps its entire paste in <b style="font-weight:normal"> — skip it
      if (/font-weight\s*:\s*normal/i.test(style)) return inner();
      const t = inner().trim();
      return t ? `**${t}**` : '';
    }
    case 'em': case 'i': { const t = inner().trim(); return t ? `*${t}*` : ''; }
    case 'u': return `<u>${inner()}</u>`;
    case 's': case 'del': case 'strike': { const t = inner().trim(); return t ? `~~${t}~~` : ''; }
    case 'sup': return `<sup>${inner()}</sup>`;
    case 'sub': return `<sub>${inner()}</sub>`;
    case 'code': return `\`${el.textContent ?? ''}\``;
    case 'pre':  return `\n\`\`\`\n${el.textContent ?? ''}\n\`\`\`\n`;
    case 'a': {
      const href = el.getAttribute('href') ?? '';
      return href && !href.startsWith('#') ? `[${inner()}](${href})` : inner();
    }
    case 'h1': return `\n# ${inner({ inHeading: true }).trim()}\n`;
    case 'h2': return `\n## ${inner({ inHeading: true }).trim()}\n`;
    case 'h3': return `\n### ${inner({ inHeading: true }).trim()}\n`;
    case 'h4': return `\n#### ${inner({ inHeading: true }).trim()}\n`;
    case 'h5': return `\n##### ${inner({ inHeading: true }).trim()}\n`;
    case 'h6': return `\n###### ${inner({ inHeading: true }).trim()}\n`;
    case 'p':   return `\n${inner().trim()}\n`;
    case 'div': return `\n${inner().trim()}\n`;
    case 'br':  return '\n';
    case 'hr':  return '\n---\n';
    case 'blockquote':
      return `\n${inner().trim().split('\n').map(l => `> ${l}`).join('\n')}\n`;
    case 'table': {
      const rows = Array.from(el.querySelectorAll('tr'));
      if (!rows.length) return '';
      const toRow = (r: Element) =>
        '| ' + Array.from(r.querySelectorAll('td,th')).map(c => c.textContent?.trim() ?? '').join(' | ') + ' |';
      const sep = '| ' + Array.from(rows[0].querySelectorAll('td,th')).map(() => '---').join(' | ') + ' |';
      return `\n${toRow(rows[0])}\n${sep}\n${rows.slice(1).map(toRow).join('\n')}\n`;
    }
    case 'ul':
    case 'ol': {
      const ordered = tag === 'ol';
      const indent = '  '.repeat(ctx.listDepth);
      const childCtx: HtmlCtx = { ...ctx, listDepth: ctx.listDepth + 1 };
      const lines: string[] = [];
      let idx = 0;
      for (const child of Array.from(el.childNodes)) {
        const childTag = (child as HTMLElement).tagName?.toLowerCase();
        if (childTag === 'li') {
          // Separate inline content from any nested lists inside this li (standard HTML)
          let liText = '';
          let nestedMd = '';
          for (const n of Array.from((child as HTMLElement).childNodes)) {
            const t = (n as HTMLElement).tagName?.toLowerCase();
            if (t === 'ul' || t === 'ol') {
              nestedMd += '\n' + convertNode(n, childCtx).trimEnd();
            } else {
              liText += convertNode(n, childCtx);
            }
          }
          const marker = ordered ? `${++idx}. ` : '- ';
          lines.push(`${indent}${marker}${liText.trim()}${nestedMd}`);
        } else if (childTag === 'ul' || childTag === 'ol') {
          // Google Docs puts nested lists as siblings of <li>, not as children
          const nestedMd = convertNode(child, childCtx).trimEnd();
          if (lines.length > 0) {
            lines[lines.length - 1] += '\n' + nestedMd;
          } else {
            lines.push(nestedMd.trimStart());
          }
        }
      }
      return `\n${lines.join('\n')}\n`;
    }
    case 'span': {
      let content = inner();
      // Heading-sized text — only when not already inside a semantic heading tag
      if (!ctx.inHeading) {
        const prefix = headingPrefixFromPx(parseFontSizePx(style));
        if (prefix) return `\n${prefix}${content.trim()}\n`;
      }
      if (/font-weight\s*:\s*(?:bold|[6-9]\d\d)/i.test(style)) content = `**${content.trim()}**`;
      if (/font-style\s*:\s*italic/i.test(style)) content = `*${content.trim()}*`;
      if (/text-decoration[^:]*:\s*[^;]*underline/i.test(style)) content = `<u>${content}</u>`;
      if (/text-decoration[^:]*:\s*[^;]*line-through/i.test(style)) content = `~~${content.trim()}~~`;
      return content;
    }
    case 'wbr': return '';
    case 'script': case 'style': case 'head': return '';
    default: return inner();
  }
}

function htmlToMarkdown(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return convertNode(doc.body).replace(/\n{3,}/g, '\n\n').trim();
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-');
}

function buildInitialContent(args: {
  title: string;
  description: string;
  category: string;
  subcategory: string;
  audience: Set<'farmer' | 'shopper'>;
  slug: string;
  source: ContentSource;
  pasteText: string;
  claudePrompt: string;
}): string {
  const { title, description, category, subcategory, audience, slug, source, pasteText, claudePrompt } = args;

  const today = new Date().toISOString().slice(0, 10);
  const escapedSummary = description.replace(/"/g, '\\"');
  const categoryLine = subcategory
    ? [`  - key: ${category}`, `    subcategory: ${subcategory}`]
    : [`  - key: ${category}`];
  const prompt = source === 'claude' ? claudePrompt.trim() : '';
  const frontmatter = [
    '---',
    `id: '${crypto.randomUUID()}'`,
    `title: "${title.replace(/"/g, '\\"')}"`,
    `slug: ${slug}`,
    `categories:`,
    ...categoryLine,
    `summary: "${escapedSummary}"`,
    `owner: unset`,
    `last_reviewed: '${today}'`,
    `audience:`,
    ...[...audience].map(a => `  - ${a}`),
    `verified: false`,
    `seo:`,
    `  description: "${escapedSummary}"`,
    // claude_brief is stored in frontmatter so the body comment stays single-line
    ...(prompt ? [`claude_brief: "${prompt.replace(/"/g, '\\"')}"`] : []),
    '---',
    '',
  ].join('\n');

  if (source === 'paste') {
    return frontmatter + (pasteText.trim() || '');
  }

  if (source === 'claude') {
    const body = prompt
      ? '{/* CLAUDE: Please write this article based on the claude_brief frontmatter field. */}\n\n'
      : '{/* CLAUDE: Please write this article. */}\n\n';
    return frontmatter + body;
  }

  // scratch
  return frontmatter + `## Overview\n\n`;
}

interface Props {
  open: boolean;
  token: string;
  onClose: () => void;
  onAuthError: () => void;
  onStartEditing: (filePath: string, initialContent: string, prTitle: string) => void;
  onCreated: (result: { prNumber: number; commitSha: string }) => void;
}

export function NewArticleDialog({ open, token, onClose, onAuthError, onStartEditing, onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [audience, setAudience] = useState<Set<'farmer' | 'shopper'>>(new Set(['farmer']));
  const [source, setSource] = useState<ContentSource>('scratch');
  const [pasteText, setPasteText] = useState('');
  const [claudePrompt, setClaudePrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const slug = slugify(title);
  const selectedCategory = CATEGORIES.find((c) => c.key === category);
  const subcategories = selectedCategory?.subcategories ?? [];
  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && category.length > 0 && slug.length > 0;

  const handleCategoryChange = (key: string) => {
    setCategory(key);
    setSubcategory('');
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    const filePath = `content/en/${category}/${slug}.mdx`;
    const prTitle = title.trim();
    const initialContent = buildInitialContent({
      title: prTitle,
      description: description.trim(),
      category,
      subcategory,
      audience,
      slug,
      source,
      pasteText,
      claudePrompt,
    });

    if (source === 'claude') {
      setSubmitting(true);
      try {
        const res = await api.directPr(token, {
          filePath,
          mdxContent: initialContent,
          prTitle,
        });
        resetForm();
        onClose();
        onCreated(res);
        toast.success('Claude is now drafting the article. Check back and refresh in a moment to see the preview.');
      } catch (e) {
        if (e instanceof AuthError) { onAuthError(); return; }
        toast.error('Failed to create article', {
          description: e instanceof Error ? e.message : 'unknown error',
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    onStartEditing(filePath, initialContent, prTitle);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCategory('');
    setSubcategory('');
    setAudience(new Set(['farmer']));
    setSource('scratch');
    setPasteText('');
    setClaudePrompt('');
  };

  const inputCls = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
  const labelCls = 'block text-sm font-medium mb-1';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { resetForm(); onClose(); } }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create new article</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className={labelCls}>Title <span className="text-destructive">*</span></label>
            <input
              className={inputCls}
              placeholder="e.g. How to add a CSA share"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            {slug && (
              <p className="mt-1 text-xs text-muted-foreground">
                Slug: <span className="font-mono">{slug}</span>
              </p>
            )}
          </div>

          <div>
            <label className={labelCls}>Summary <span className="text-destructive">*</span></label>
            <input
              className={inputCls}
              placeholder="One-sentence summary shown in search results"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className={subcategories.length > 0 ? 'flex gap-3' : ''}>
            <div className={subcategories.length > 0 ? 'flex-1' : ''}>
              <label className={labelCls}>Category <span className="text-destructive">*</span></label>
              <select
                className={inputCls}
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value)}
              >
                <option value="">Select a category</option>
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>

            {subcategories.length > 0 && (
              <div className="flex-1">
                <label className={labelCls}>Subcategory</label>
                <select
                  className={inputCls}
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                >
                  <option value="">None</option>
                  {subcategories.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className={labelCls}>Audience</label>
            <div className="flex gap-2">
              {(['farmer', 'shopper'] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAudience((prev) => {
                    const next = new Set(prev);
                    if (next.has(a)) {
                      if (next.size > 1) next.delete(a);
                    } else {
                      next.add(a);
                    }
                    return next;
                  })}
                  className={cn(
                    'flex-1 rounded-md border px-3 py-2 text-sm capitalize transition-colors',
                    audience.has(a)
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background hover:bg-accent',
                  )}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>Starting content</label>
            <div className="flex gap-2 mb-3">
              {([
                { key: 'scratch', label: 'From scratch' },
                { key: 'paste', label: 'Paste existing' },
                { key: 'claude', label: 'Generate with Claude' },
              ] as const).map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSource(s.key)}
                  className={cn(
                    'flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors',
                    source === s.key
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-input bg-background hover:bg-accent',
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {source === 'paste' && (
              <Textarea
                placeholder="Paste the existing article text here"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                onPaste={(e) => {
                  const html = e.clipboardData.getData('text/html');
                  if (!html) return;
                  e.preventDefault();
                  const markdown = htmlToMarkdown(html);
                  const ta = e.currentTarget;
                  const before = ta.value.slice(0, ta.selectionStart ?? 0);
                  const after = ta.value.slice(ta.selectionEnd ?? 0);
                  setPasteText(before + markdown + after);
                }}
                rows={6}
              />
            )}

            {source === 'claude' && (
              <Textarea
                placeholder="Describe what this article should cover (Claude will draft from this brief)"
                value={claudePrompt}
                onChange={(e) => setClaudePrompt(e.target.value)}
                rows={4}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onClose(); }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Creating…' : source === 'claude' ? 'Create article' : 'Open editor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
